/***
 *
 *   TERMS  (Terms of Use & Acceptable Use)
 *   Covers all MapAtlas services in the portal: APIs, the MCP connector,
 *   plugins and Studio. Plain-language, standard API terms (data ownership +
 *   acceptable use + abuse protection), written to be clear, not alarming.
 *
 *   NOTE: This is a starting template, NOT legal advice. Have counsel review
 *   before relying on it.
 *
 **********/

import { Fragment, useEffect } from "react";

const COMPANY = "MapMetrics";
const SERVICE = "MapAtlas";
const CONTACT = "legal@mapmetrics.org";
const UPDATED = "9 June 2026";

let SECTION_N = 0;

function Section({ title, children }) {
  SECTION_N += 1;
  const n = SECTION_N;
  return (
    <section className="mb-7">
      <h2 className="text-[16px] font-semibold text-white mb-2.5">
        <span className="text-violet-300 mr-2">{n}.</span>{title}
      </h2>
      <div className="space-y-2.5 text-[13.5px] leading-relaxed text-white/75">
        {children}
      </div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5 marker:text-violet-300/60">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

export function Terms() {
  SECTION_N = 0; // reset auto-numbering each render
  useEffect(() => {
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "auto";
      document.documentElement.style.overflowX = "auto";
    };
  }, []);

  return (
    <Fragment>
      <div className="min-h-screen w-full overflow-x-hidden" style={{ background: "#1b2236" }}>
        <div className="py-3 w-full box-border px-6 overflow-x-hidden">
          <div className="rounded-2xl p-7 sm:p-9 max-w-3xl border border-white/10"
            style={{ background: "rgba(30,37,56,0.97)" }}>
            <div className="mb-6">
              <h1 className="text-[24px] font-semibold text-white">Terms of Use</h1>
              <p className="text-[12px] text-white/45 mt-1">Last updated: {UPDATED}</p>
            </div>

            <p className="text-[13.5px] leading-relaxed text-white/75 mb-7">
              These terms cover your use of the {SERVICE} platform and everything in it:
              the APIs, the MCP connector, plugins and Studio (together, the
              "Services"), provided by {COMPANY}. By creating an account, generating an
              API key or MCP link, or connecting an AI assistant, you agree to these
              terms. They are meant to be readable, so you know exactly what you can do
              and how we keep the platform fair for everyone.
            </p>

            <Section title="The Services">
              <p>
                {SERVICE} provides geospatial capabilities, geocoding and reverse
                geocoding, search and places, routing, matrices, isochrones and related
                tools. You can use them through any part of the portal, including:
              </p>
              <Bullets items={[
                "REST APIs and API keys for direct integration.",
                "The MCP connector, for connecting AI assistants and agents.",
                "Studio, for designing, editing and hosting your own map styles and projects.",
                "Analytics and usage dashboards.",
                "Any other plugins, connectors or tools we make available in the portal.",
              ]} />
              <p>
                These are together the "Services". We may add, change or remove features
                over time. These terms apply to all of them, however you access them.
              </p>
            </Section>

            <Section title="Your account, keys and MCP link">
              <p>
                You are responsible for activity under your account, your API keys and
                your personal MCP link. Treat them like passwords:
              </p>
              <Bullets items={[
                "Keep your keys and MCP link private. Anyone who has them can use your requests.",
                "You can rotate or revoke a key or link at any time from the portal.",
                "You are responsible for your end users and for the applications you build on the Services.",
              ]} />
            </Section>

            <Section title="Plans, requests and fair use">
              <p>
                Each plan includes a monthly pool of requests shared across every API.
                Heavier calls (for example, a place or nearby search) count as more than
                one request. If you go over your plan, additional usage is billed
                per call, or you can move to a higher plan at any time.
              </p>
              <p>
                To keep the platform fast and reliable for everyone, plans also include
                reasonable rate limits. Sustained traffic far beyond normal application
                use, or attempts to get around quotas or rate limits, is not permitted.
              </p>
            </Section>

            <Section title="Our data and your licence">
              <p>
                The map data, points of interest, place records, and all other content
                returned by the Services (the "{SERVICE} Data") is owned by {COMPANY} or
                its licensors and remains our property. Using the Services does not
                transfer ownership of any of it to you.
              </p>
              <p>
                We grant you a non-exclusive, non-transferable licence to use {SERVICE}{" "}
                Data within your own application or workflow, for the purpose the
                Services are designed for, for as long as your account is in good
                standing. You may show results to your end users as part of your product.
              </p>
            </Section>

            <Section title="Your content (Studio and projects)">
              <p>
                The map styles, designs, projects and other materials you create in
                Studio or upload to the portal ("Your Content") belong to you. You grant{" "}
                {COMPANY} the licence needed to host, process, back up and serve Your
                Content so the Services can work, and you are responsible for having the
                rights to anything you upload.
              </p>
              <p>
                Your Content is often built on top of {SERVICE} Data (for example, a style
                applied to our maps, or a project that references our places). The {SERVICE}{" "}
                Data underneath remains ours as described above; your ownership covers your
                own design and additions, not the underlying data.
              </p>
            </Section>

            <Section title="Payments">
              <p>
                Paid plans and pay-as-you-go usage are billed through our payment
                processor. You authorise us to charge your chosen payment method for the
                plan you select and for any usage beyond it. Fees are exclusive of taxes
                unless stated, and except where required by law are non-refundable. We may
                update pricing with reasonable notice; changes do not affect the period
                you have already paid for.
              </p>
            </Section>

            <Section title="Acceptable use">
              <p>So that the Services stay available and our data stays protected, you agree not to:</p>
              <Bullets items={[
                "Bulk-extract, scrape, harvest, or systematically download the " + SERVICE + " Data, or use the Services to enumerate large areas rather than answer specific queries.",
                "Build, train, populate or augment a competing map, places or location dataset using the " + SERVICE + " Data.",
                "Resell, sublicense, or redistribute the raw " + SERVICE + " Data as a standalone dataset, separate from your application.",
                "Store " + SERVICE + " Data beyond the limited caching described below, or build a permanent local copy of the dataset.",
                "Circumvent, disable or interfere with quotas, rate limits, access controls or security measures.",
                "Reverse engineer the Services, or remove any required attribution.",
                "Use the Services unlawfully, or in a way that infringes the rights of others or your end users.",
              ]} />
            </Section>

            <Section title="Caching and storage">
              <p>
                You may cache {SERVICE} Data temporarily where it genuinely improves your
                application's performance, for example holding a result for the duration
                of a user session or a short period afterwards. You may keep a record
                that links your own content to a {SERVICE} place where needed for your
                app to function. What you should not do is accumulate cached results into
                a standing copy of the dataset.
              </p>
            </Section>

            <Section title="Keeping the platform fair">
              <p>
                We monitor usage of the Services to keep them reliable and to protect the{" "}
                {SERVICE} Data, in the same way other API and map providers do. If usage
                looks like abuse, such as automated bulk extraction or activity that
                threatens the service, we may apply rate limits, request that it stops,
                or suspend access. Wherever possible we will reach out first. These
                measures exist to protect normal customers and our data, not to get in
                the way of legitimate use.
              </p>
            </Section>

            <Section title="Attribution">
              <p>
                Where attribution is required for {SERVICE} Data or its underlying
                sources, you agree to keep it visible in your application as indicated in
                our documentation.
              </p>
            </Section>

            <Section title="Third-party services">
              <p>
                The Services rely on and connect to third parties, including our payment
                processor, the providers of underlying map data, and any AI assistants,
                clients or tools you choose to connect (for example through the MCP
                connector). Those third parties have their own terms, and we are not
                responsible for them. You are responsible for your use of the tools you
                connect to {SERVICE}.
              </p>
            </Section>

            <Section title="Privacy and end users">
              <p>
                {COMPANY} is EU-hosted and handles personal data in line with applicable
                data-protection law, including the GDPR, as described in our Privacy
                Policy. If you send us data about your own end users through the Services,
                you are responsible for having the right to do so and for informing them
                as required.
              </p>
            </Section>

            <Section title="MCP and plugins">
              <p>
                When you connect an AI assistant or plugin using your MCP link, calls made
                through that connection count against your plan and are your
                responsibility, just like direct API calls. You are responsible for how
                the assistants and tools you connect use the Services.
              </p>
            </Section>

            <Section title="Availability, changes and beta features">
              <p>
                We work to keep the Services available but do not guarantee they will be
                uninterrupted or error-free. Features marked beta or experimental may
                change or be withdrawn. We may update the Services and these terms; if we
                make a material change we will give reasonable notice, and continued use
                means you accept the update.
              </p>
            </Section>

            <Section title="Suspension and termination">
              <p>
                You can stop using the Services and close your account at any time. We may
                suspend or terminate access if you breach these terms, in particular the
                acceptable-use section, or where required to protect the platform or
                comply with the law.
              </p>
            </Section>

            <Section title="Disclaimers and liability">
              <p>
                The Services and {SERVICE} Data are provided "as is". To the extent
                permitted by law, {COMPANY} disclaims implied warranties and is not liable
                for indirect or consequential loss. Nothing in these terms limits
                liability that cannot be limited by law.
              </p>
            </Section>

            <Section title="Contact">
              <p>
                Questions about these terms? Reach us at{" "}
                <a href={`mailto:${CONTACT}`} className="text-violet-300 underline">{CONTACT}</a>.
              </p>
            </Section>

            <p className="text-[11px] text-white/35 mt-8 pt-5 border-t border-white/[0.07]">
              This page is a plain-language summary of your agreement with {COMPANY} for
              the {SERVICE} Services. It does not constitute legal advice.
            </p>
          </div>
        </div>
      </div>
    </Fragment>
  );
}
